import { userUtils, utils } from '../../../helpers';
import { User } from '@/legacy-types';
import { TextingSettings } from './TextingSettingsPage';
import { safeFetchAPI } from '@/api';
import { TextingImageContentScanResults } from '@/types';

export function getUserTextingSettings(user: User): TextingSettings {
	return {
		optinMessage: user?.optinMessage || 'Reply Y or YES to confirm your account and turn on notifications. Msg & data rates may apply.',
		tcmpID: user?.tcmpID,
		postOptinMessage: user?.postOptinMessage || 'You have successfully been opted in.',
		msgContainer: user?.msgContainer || 'You have a new notification: {{landingPage}}',
		walletLoginMessage: user?.walletLoginMessage || user?.msgContainer || 'You have a new notification: {{landingPage}}',
		replySTOP: user?.replySTOP || 'Reply STOP to opt out',
		manualNums: user?.manualNums ?? false,
		allowBanned: user?.allowBanned ?? false,
		impliedConsent: user?.impliedConsent || { enabled: false },
		disableWalledGarden: user?.disableWalledGarden ?? false,
		requirePhoneCaptcha: user?.requirePhoneCaptcha ?? false,
		message2FA: user?.message2FA ?? false,
		messageDOB: user?.messageDOB ?? false,
		mmsLogo: user?.mmsLogo ?? false,
		optinImage: user?.optinImage ?? '',
		shortyPrefix: user?.shortyPrefix ?? '',
		verifiedOptin: user?.verifiedOptin ?? false,
		optInAttempts: user?.optInAttempts,
		disableShorty: user?.disableShorty ?? false,
		infobipCmpID: user?.infobipCmpID || '',
		autoResponder: user?.autoResponder || {},
		macroFallback: user?.macroFallback || {},
		nickname: user?.nickname || '',
		skipProviderRestrictions: user?.skipProviderRestrictions ?? false,
		enableMessageInbox: user?.enableMessageInbox ?? false,
	};
}

export function disableFieldForMessageDigitial() {
	const isOwnerOrAbove = userUtils.hasRoleView('owner');
	const isMessageDigitial = utils.isMessageDigital();
	const disableForMDO = isMessageDigitial && !isOwnerOrAbove;
	return disableForMDO;
}

export async function isTextingImageContentFlagged(imageURL: string): Promise<boolean> {
	// If no image then it's not flagged
	if (!imageURL || utils.isLocal() || utils.isDevEnv()) {
		return false;
	}

	// If they are in canada they skip this check
	if (utils.isCanadaBilling()) {
		return false;
	}

	// If they are not in the cananbis industry they skip this check
	if (!utils.isCannabis()) {
		return false;
	}

	utils.toaster?.addToast?.({
		duration: 0,
		toastKey: 'scanning-image',
		message: 'Verifying image content is compliant with cannabis regulations...',
		type: 'info',
	});

	// Scan the image first for any regulated industry content
	const [context, error] = await safeFetchAPI<TextingImageContentScanResults>('/scanTextingImageContent/:uid', {
		method: 'PUT',
		payload: {
			imageURL,
		},
	});

	utils?.toaster?.removeToast?.('scanning-image');

	if (error) {
		utils?.toaster?.addToast?.({
			message: error || 'Error scanning image content',
			type: 'danger',
		});
		return true;
	}

	const isLogo = context?.logoLikelihood && context.logoLikelihood > 59;
	const isFlagged = context?.flaggedLikelihood && context.flaggedLikelihood > 50;
	const isSuperFlagged = context?.flaggedLikelihood && context.flaggedLikelihood > 80;
	const isCannabisRelated = context?.cannabisRelatedLikelihood && context.cannabisRelatedLikelihood > 50;
	const isCannabisPromotion = context?.cannabisPromotionLikelihood && context.cannabisPromotionLikelihood > 50;

	if (isSuperFlagged || isCannabisRelated || isCannabisPromotion || isFlagged) {
		if ((isLogo && !isFlagged) || (!isCannabisPromotion && !isFlagged)) {
			utils?.toaster?.addToast?.({
				duration: 15 * 1000,
				message: 'The logo you have uploaded has been marked as "somewhat risky" and may be restricted by carriers',
				type: 'warning',
			});
			return false;
		}
		if (userUtils.isExperimental() && userUtils.isSuperAdmin() && userUtils.debugMode()) {
			utils?.toaster?.addToast?.({
				duration: 5 * 1000,
				message: 'Image would be blocked but has been allowed due to admin bypass',
				type: 'warning',
			});
			return false;
		}
		utils?.toaster?.addToast?.({
			duration: 20 * 1000,
			message:
				'The image you are trying to upload contains content that is restricted by carriers and is likely to cause you to get fined or banned from 10dlc telecom networks. We have blocked this image being selected as a result. Please use clean company logos for your texting logo and put all regulated content within your html landing page that users navigate to after clicking the link in your text. Thank you',
			type: 'danger',
		});
		return true;
	}

	return false;
}
